import { gql } from "@apollo/client/core";
import {
    getProductCollectionProductsQuery,
    getProductCollectionProductsFilteredQuery,
    getProductCollectionProductsFeedQuery,
    productCollectionFieldsFragment,
} from "./Bundle/query";
import { metafieldManager } from "./services/metafield";
import { graphQlManager } from "./services/server";
import {
    IntegrationProductResource,
    IntegrationType,
    ProductCollectionResource,
    Product,
    ProductWorkflow as ProductWorkflowInterface,
    ProductCollectionProductResource,
    TransformCollection as TransformCollectionInterface,
    TransformCollectionTransform,
    BundleStateTransform,
    Asset,
} from "./types";
import { ClientError } from "./util/exception";

interface ProductMetafieldFilter {
    operator: "Equals" | "IsEmpty";
    invert?: boolean;
    metafieldConfigurationId: string;
    value?: string;
}

interface ProductTagFilter {
    /** Only products with tags matching all entries in this array will be included. */
    include: string[];
    /** Only products that have no tags matching any entry in this array will be included. */
    exclude: string[];
}

export enum ProductCollectionProductSortKey {
    Default = "Default",
    Name = "Name",
    Price = "Price",
}

interface FilteredProductCollectionQueryResponse {
    productCollections: {
        id: string;
        productCollectionProducts: { id: string; product: { id: string } }[];
    }[];
}

/**
 * A collection of products that can be used to form a bundle.
 */
export class ProductCollection {
    private readonly collection: ProductCollectionResource;
    private fullFetched = false;

    constructor(collection: ProductCollectionResource) {
        this.collection = collection;
        // When the collection passed in as the basis for this class
        // contains products we know the products were eagerly fetched.
        const products = this.collection.productCollectionProducts;
        if (products && products.length > 0) {
            this.fullFetched = true;
        }
    }

    /**
     * The ID of the product collection.
     */
    getId(): string {
        return this.collection.id;
    }

    /**
     * The name of the collection.
     */
    getName(): string {
        return this.collection.name;
    }

    /**
     * The description of the collection.
     */
    getDescription(): string {
        return this.collection.description || "";
    }

    /**
     * The image of the collection.
     */
    getImage(): undefined | Asset {
        return this.collection.image;
    }

    /**
     * A list of products in this collections with useful helpers for interacting with them.
     */
    getProducts(): CollectionProduct[] {
        if (!this.collection.productCollectionProducts) {
            throw new Error("Failed to find products on collection. Ensure you fetch them first!");
        }
        return this.collection.productCollectionProducts
            .filter((pcp) => !!pcp.product)
            .map((pcp) => {
                return new CollectionProduct(pcp);
            });
    }

    /**
     * A list of products in this collections with useful helpers for interacting with them.
     */
    async fetchProducts(productIds?: string[]): Promise<CollectionProduct[]> {
        if (this.fullFetched) {
            return this.collection!.productCollectionProducts!.filter((pcp) => !!pcp.product).map((pcp) => {
                return new CollectionProduct(pcp);
            });
        }
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .query<{ productCollections: ProductCollectionResource[] }>({
                query: getProductCollectionProductsQuery,
                variables: {
                    id: this.getId(),
                    productIds,
                },
                errorPolicy: "all",
            });
        if (!productIds) {
            this.fullFetched = true;
        }
        this.collection.productCollectionProducts = response.data.productCollections[0].productCollectionProducts || [];
        return this.collection.productCollectionProducts
            .filter((pcp) => !!pcp.product)
            .map((pcp) => {
                return new CollectionProduct(pcp);
            });
    }

    /**
     * Returns a list of collections products matching the associated metafield/tag filters.
     * @param filters A list of metafield filters to apply.
     * @param tags An object of tag filters to apply.
     */
    async filterProducts(
        filters?: ProductMetafieldFilter[],
        tags?: ProductTagFilter,
        sortKey?: ProductCollectionProductSortKey,
        sortDescending?: boolean,
    ) {
        const response = await graphQlManager.getShadowGraphqlClient().query<FilteredProductCollectionQueryResponse>({
            query: getProductCollectionProductsFilteredQuery,
            errorPolicy: "all",
            variables: {
                id: this.getId(),
                filters: filters
                    ? {
                          link: "And",
                          metafields: filters,
                      }
                    : undefined,
                tags: tags
                    ? {
                          include: tags.include ?? [],
                          exclude: tags.exclude ?? [],
                      }
                    : undefined,
                sortKey: sortKey,
                sortDescending: sortDescending,
            },
        });
        if (!response.data?.productCollections || response.data?.productCollections.length === 0) {
            throw new Error("Failed to filter product collection products.");
        }
        const currentProducts = this.getProducts();
        const filteredResults = response.data.productCollections[0].productCollectionProducts.map(
            (it) => it.product.id,
        );
        return currentProducts.filter((it) => filteredResults.includes(it.getId()));
    }

    /**
     * Fetches a paginated feed of products.
     * @param offset The zero-based start index.
     * @param limit The maximum number of products to return.
     * @param filters Optional metafield filters to apply to the query.
     * @param tags Optional object of tag filters to apply.
     * @returns
     */
    async fetchProductsFeed(
        offset: number,
        limit: number,
        filters?: ProductMetafieldFilter[],
        tags?: ProductTagFilter,
        sortKey?: ProductCollectionProductSortKey,
        sortDescending?: boolean,
    ): Promise<{
        items: CollectionProduct[];
        total: number;
    }> {
        if (this.fullFetched) {
            // Local pagination
            const filteredProducts = await (filters ? this.filterProducts(filters) : this.fetchProducts());
            return {
                total: filteredProducts.length,
                items: filteredProducts.slice(offset, offset + limit),
            };
        }
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .query<{ productCollections: ProductCollectionResource[] }>({
                query: getProductCollectionProductsFeedQuery,
                variables: {
                    id: this.getId(),
                    limit,
                    offset,
                    filters: filters
                        ? {
                              link: "And",
                              metafields: filters,
                          }
                        : undefined,
                    tags: tags
                        ? {
                              include: tags.include ?? [],
                              exclude: tags.exclude ?? [],
                          }
                        : undefined,
                    sortKey: sortKey,
                    sortDescending: sortDescending,
                },
                errorPolicy: "all",
            });
        const items =
            response?.data?.productCollections?.[0].productCollectionProductsFeed?.items
                .filter((pcp) => !!pcp.product)
                .map((pcp) => {
                    return new CollectionProduct(pcp);
                }) || [];
        return {
            items,
            total: response?.data?.productCollections?.[0].productCollectionProductsFeed?.total ?? 0,
        };
    }

    getTransformCollection(): TransformCollection | undefined {
        if (!this.collection.transformCollection) return undefined;
        return new TransformCollection(this.collection.transformCollection);
    }

    /**
     * The raw collection resource. This is generally not needed and should be avoided.
     */
    getResource(): ProductCollectionResource {
        return this.collection;
    }
}

/**
 * A collection product is a product within a ProductCollection. It provides a simple interface for interacting with the product.
 */
export class CollectionProduct {
    private readonly product: Product;
    private readonly productResource: ProductCollectionProductResource;

    constructor(productCollectionProduct: ProductCollectionProductResource) {
        this.product = productCollectionProduct.product; // Code should not attempt to use the CollectionProduct() constructor on PCPs with a null product.
        this.productResource = productCollectionProduct;
    }

    /**
     * The ID of the product in SpiffCommerce.
     * @returns
     */
    getId(): string {
        return this.product.id;
    }

    /**
     * The name of the product. Human readable.
     */
    getName(): string {
        return this.product.name;
    }

    /**
     * A helper function for getting integrations
     * @param type The type of integration you want.
     * @param externalId An external integration ID to further filter by. Otherwise we return the first encountered.
     * @returns The integration if found. Throws an error if not found as this data is typically a neccesity.
     */
    getIntegrationByType(type: IntegrationType, externalId?: string): IntegrationProductResource {
        const ips = this.product.integrationProducts || [];
        const ip = ips.find((ip) => {
            const hasType = ip.integration?.type;
            const typeMatches = ip.integration?.type === type;
            const externalIdMatches = externalId ? ip.integration?.externalIntegrationId === externalId : true;
            return hasType && typeMatches && externalIdMatches;
        });
        if (!ip) {
            throw new Error(
                "Failed to find requested integration type on product. This is generally due to a configuration error",
            );
        }
        return ip;
    }

    getCurrentIntegration(): IntegrationProductResource {
        const ips = this.product.integrationProducts || [];
        const ip = ips.find((ip) => ip.integration?.isCurrent);
        if (!ip) {
            throw new Error(
                `Product: ${this.productResource.id} is not linked to this current integration. This is generally due to a configuration error.`,
            );
        }
        return ip;
    }

    /**
     * A helper function for pulling the default workflow.
     */
    getDefaultWorkflow(): ProductWorkflow {
        const workflows = this.product.workflows;

        if (workflows === undefined) {
            throw new ClientError(
                "Called getDefaultWorkflow() before fetching collection products. Use collection.fetchProducts() first to ensure the data is available.",
            );
        }

        if (workflows.length === 0) {
            throw new ClientError(
                "Called getDefaultWorkflow() but no workflows on requested product. Ensure at least 1 workflow is assigned to this product.",
            );
        }

        // Check for a default configured on the collection
        const configuredDefault = this.productResource.workflowId;
        if (configuredDefault) {
            const found = workflows.find((w) => w.workflowName === configuredDefault);
            if (found) return new ProductWorkflow(found);
        }

        if (workflows.length === 1) {
            console.warn(
                "Called getDefaultWorkflow() but no default was configured. There is only one workflow available so we will fall back to using this!",
            );
            return new ProductWorkflow(workflows[0]);
        }

        throw new ClientError(
            "Called getDefaultWorkflow() but no default workflow is configured for the requested product. Multiple workflows exist so we can't assume!",
        );
    }

    /**
     * @returns All workflows associated with this Product.
     */
    getAllWorkflows(): ProductWorkflow[] {
        const workflows = this.product.workflows || [];

        if (workflows.length === 0) {
            throw new ClientError(
                "No workflows on configured product. Ensure at least 1 workflow is assigned to this product.",
            );
        }
        return this.product
            .workflows!.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            .map((workflow) => new ProductWorkflow(workflow));
    }

    /**
     * A list of all integrations this product is connected to.
     */
    getIntegrations(): IntegrationProductResource[] {
        return this.product.integrationProducts || [];
    }

    /**
     * The raw product resource. This is generally not needed and should be avoided.
     */
    getResource(): Product {
        return this.product;
    }

    /**
     * Gets the base price of the product. Optionally include additional product pricing.
     * @param includeAdditionalProduct Optionally include additional product pricing. You must provide at least one of the fields on this object.
     * @returns
     */
    getBasePrice(includeAdditionalProduct?: {
        /**
         * The integration type to locate the additional product on.
         */
        integrationType?: IntegrationType;
        /**
         * The external ID of the integration to locate the additional product on.
         */
        externalId?: string;
        /**
         * The internal Spiff integration ID to locate the additional product on.
         */
        integrationId?: string;
    }): number {
        const price = this.product.basePrice || 0;
        if (!includeAdditionalProduct) return price;
        if (
            !includeAdditionalProduct.integrationType &&
            !includeAdditionalProduct.externalId &&
            !includeAdditionalProduct.integrationId
        ) {
            throw new Error(
                "You must provide at least one of the following fields on the includeAdditionalProduct object: integrationType, externalId, integrationId",
            );
        }
        let ip: IntegrationProductResource | undefined;
        if (includeAdditionalProduct.integrationId) {
            ip = this.product.integrationProducts?.find(
                (ip) => ip.integration?.id === includeAdditionalProduct.integrationId,
            );
        } else if (includeAdditionalProduct.externalId) {
            ip = this.product.integrationProducts?.find(
                (ip) => ip.integration?.externalIntegrationId === includeAdditionalProduct.externalId,
            );
        } else {
            ip = this.product.integrationProducts?.find(
                (ip) => ip.integration?.type === includeAdditionalProduct.integrationType,
            );
        }
        if (!ip?.additionalIntegrationProduct?.product) return price;
        return price + (ip.additionalIntegrationProduct.product.basePrice || 0);
    }

    /**
     * Requests a set of metafields for this product. Metafields that are not available locally will be fetched from the server.
     * @param keys The set of metafield keys to request.
     * @returns A map of metafield key to value. Any unavailable keys will not be present in the map. use map.has() to check if a key is present.
     */
    async requestMetafields(keys: string[]): Promise<Map<string, string>> {
        return metafieldManager.requestKeysForEntity(this.product.id, keys);
    }
}

/**
 * Represents a the relationship between a workflow and a product.
 */
export class ProductWorkflow {
    private readonly workflow: ProductWorkflowInterface;

    constructor(workflow: ProductWorkflowInterface) {
        this.workflow = workflow;
    }

    /**
     * @returns The ID of this workflow, to be used when starting an experience.
     */
    getId(): string {
        return this.workflow.workflowName;
    }

    /**
     * @returns The name of the workflow.
     */
    getName(): string {
        return this.workflow.friendlyName;
    }

    /**
     * An image to be used to display this workflow in a UI to the customer.
     * @returns A resource URL.
     */
    getThumbnail(): string {
        return this.workflow.imageUrl;
    }
}

/**
 * Represents a collection of transforms that can be applied inside a product collection.
 */
export class TransformCollection {
    private readonly collection: TransformCollectionInterface;
    constructor(collection: TransformCollectionInterface) {
        this.collection = collection;
    }

    /**
     * @returns The ID of the transform collection.
     */
    getId(): string {
        return this.collection.id;
    }

    /**
     * @returns The name of the transform collection.
     */
    getName(): string {
        return this.collection.name;
    }

    /**
     * @returns The transforms in this collection.
     */
    getTransforms(): Transform[] {
        return this.collection.transforms.map((t) => new Transform(t));
    }
}

/**
 * Represents the translations, rotation & scale of a product in 3D space.
 */
export class Transform {
    private readonly transform: TransformCollectionTransform;
    constructor(position: TransformCollectionTransform) {
        this.transform = position;
    }

    /**
     * @returns The ID of the transform.
     */
    getId(): string {
        return this.transform.id;
    }

    /**
     * @returns The name of the transform.
     */
    getName(): string {
        return this.transform.name;
    }

    /**
     * @returns The transformation to be used to place the object.
     */
    get(): BundleStateTransform {
        return {
            position: this.transform.position,
            rotation: this.transform.rotation,
            scale: this.transform.scale,
        };
    }
}

const getCollectionsQuery = gql`
    ${productCollectionFieldsFragment(false)}
    query GetProductCollections($ids: [String!]!) {
        productCollections(ids: $ids) {
            ...ProductCollectionFields
        }
    }
`;

export const getProductCollections = async (ids: string[]): Promise<ProductCollection[] | undefined> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        productCollections: ProductCollectionResource[];
    }>({
        query: getCollectionsQuery,
        errorPolicy: "all",
        variables: {
            ids: ids,
        },
    });
    return response.data?.productCollections?.map((it) => new ProductCollection(it));
};
